/**
 * Training Mentor Session page — renders the "Training Mentor" board for a
 * `mentor` session. Served at the mentor subdomain `/sessions/{id}`. Opening
 * the agent shows the SAME board as this app's Home page — the board is the
 * agent's whole face, not a chatbot standing in for it (same pattern as Team
 * Access / Care Planner / Patient Administration). The session id is kept only for the
 * page chrome (title, return-to-parent, share); the board itself is
 * session-agnostic apart from lighting the agent's chat side-channel.
 *
 * The board is wired to the live training surface via MentorBoardConnected; the
 * session lights up the agent's chat side-channel. The DEV /preview route keeps
 * the design fixtures.
 */

import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, Share2 } from 'lucide-react'
import { useSession } from '@frontend/hooks/useSessions'
import { useReturnToParent } from '@frontend/hooks/useSessionHandoff'
import { ReturnToParentButton } from '@frontend/components/session/ReturnToParentButton'
import { Skeleton } from '@frontend/components/ui/skeleton'
import ShareDialog from '@frontend/components/ShareDialog'
import { ReturnToMainAgent } from '@frontend/components/ReturnToMainAgent'
// The SAME face the main app mounts in-frame (it discovers this module by
// globbing apps/<slug>/src/face.tsx). Mounting it here too is what makes the
// two representations one implementation.
import Face from '../face'

export default function MentorSession() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const sessionId = id || ''

  const { data: session, isLoading, isError, refetch } = useSession(sessionId)
  const [shareOpen, setShareOpen] = useState(false)
  // "Return to session" hand-off: navigate back to the parent that spawned this
  // child, passing ?from_child so the PARENT pulls a live grounded summary
  // itself — the child posts nothing. Shared primitive (alg/care-planner/compatibility/
  // patient-admin), no re-wiring. Only meaningful (isChild) when this is a
  // child session.
  const { isChild, returnToParent } = useReturnToParent(session?.session.parent, sessionId)

  if (!id) return null

  // ERROR (no cached data) — explicit, with retry.
  if (isError && !session) {
    return (
      <div className="flex flex-col h-dvh">
        <header className="flex items-center gap-2 px-4 py-3 border-b border-neutral-200 bg-white">
          <ReturnToMainAgent className="-ml-2" />
        </header>
        <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8 text-center">
          <p className="text-[15px] text-neutral-500">{t('session.loadError', 'Could not load this conversation.')}</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="min-h-11 rounded-full bg-primary px-5 text-[15px] font-semibold text-primary-foreground transition active:scale-95"
          >
            {t('actions.retry', 'Retry')}
          </button>
        </div>
      </div>
    )
  }

  // LOADING (no cached data) — skeleton, never a blank screen.
  if (isLoading && !session) {
    return (
      <div className="flex flex-col h-dvh">
        <header className="flex items-center gap-2 px-4 py-3 border-b border-neutral-200 bg-white">
          <ReturnToMainAgent className="-ml-2" />
          <Skeleton className="h-6 w-40" />
        </header>
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-pulse text-neutral-500">{t('common.loading', 'Loading...')}</div>
        </div>
      </div>
    )
  }

  if (!session) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <p className="text-neutral-500">{t('session.notFound', 'Conversation not found')}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-dvh bg-neutral-50">
      <header className="flex items-center gap-1 border-b border-neutral-200 bg-white px-2 py-2 pt-[max(0.5rem,env(safe-area-inset-top))] sm:px-4 sm:py-3">
        <ReturnToMainAgent />
        <button
          onClick={() =>
            // On a child session the back chevron is also a return-to-parent:
            // route it through the handoff so it carries ?from_child (the parent
            // pulls the live summary). A plain history back would drop the param
            // and the parent would show no summary. Standalone sessions keep the
            // ordinary history/home back.
            isChild
              ? returnToParent()
              : window.history.length > 1
                ? navigate(-1)
                : navigate('/')
          }
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-neutral-600 transition active:scale-95 hover:bg-neutral-100"
          aria-label={t('common.back', 'Back')}
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <h1 className="min-w-0 flex-1 truncate text-base font-semibold text-neutral-900">
          {session.session.title || t('mentor.board.title')}
        </h1>
        {/* Return-to-parent hand-off — only on child sessions (shared pill). */}
        <ReturnToParentButton
          parentSessionId={session.session.parent}
          childSessionId={sessionId}
        />
        <button
          onClick={() => setShareOpen(true)}
          aria-label={t('session.share', 'Share conversation')}
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-primary transition active:scale-95 hover:bg-primary/10"
        >
          <Share2 className="h-[18px] w-[18px]" />
        </button>
      </header>

      <main className="flex-1 overflow-y-auto">
        <Face session={session} />
      </main>

      <ShareDialog
        resourceId={sessionId}
        resourceTitle={session.session.title || t('mentor.board.title')}
        resourceType="session"
        open={shareOpen}
        onOpenChange={setShareOpen}
      />
    </div>
  )
}
